/**
 * @file LegalProfilesPage.tsx
 * @description Реквизиты продавца (phase09, F14 crm.md).
 *
 * Под SHOP_MANAGE_SETTINGS. Управляет ShopLegalProfile — Юр.данные +
 * подписант + префиксы документов.
 */

import { Loader2, Plus, Star } from "lucide-react";
import { useEffect, useState } from "react";

import { LegalProfileEditDialog } from "../../../components/crm/LegalProfileEditDialog";
import { Alert } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  deleteLegalProfile,
  listLegalProfiles,
  type LegalProfileDto,
} from "../../../lib/api-crm";

export function LegalProfilesPage() {
  const notify = useNotify();
  const [items, setItems] = useState<LegalProfileDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<LegalProfileDto | null>(null);
  const [creating, setCreating] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await listLegalProfiles({ includeInactive });
      setItems(res.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  async function onDelete(p: LegalProfileDto) {
    if (!window.confirm(`Удалить профиль "${p.displayName}"?`)) return;
    try {
      await deleteLegalProfile(p.id);
      notify.success("Профиль удалён");
      void refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="label-editorial">Настройки · Реквизиты продавца</div>
        <div className="mt-1 flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-3xl font-semibold tracking-tight">Реквизиты</h1>
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4 mr-1" />
            Создать профиль
          </Button>
        </div>
        <p className="text-sm text-on-surface-variant mt-1">
          Юр.данные для УПД, счетов, актов. Должен быть как минимум один с пометкой «по умолчанию».
        </p>
      </div>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <EditorialCard>
        <EditorialCard.Body>
          <label className="flex items-center gap-2 text-sm text-on-surface-variant cursor-pointer">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="accent-primary"
            />
            Показать архивные
          </label>
        </EditorialCard.Body>
      </EditorialCard>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-on-surface-variant" />
        </div>
      ) : items.length === 0 ? (
        <EditorialCard>
          <EditorialCard.Body>
            <div className="text-center py-12 text-on-surface-variant">
              Профилей нет. Создайте первый.
            </div>
          </EditorialCard.Body>
        </EditorialCard>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((p) => (
            <EditorialCard key={p.id}>
              <EditorialCard.Body className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-semibold tracking-tight truncate">
                      {p.displayName}
                    </h2>
                    {p.isDefault ? (
                      <Badge variant="secondary" className="gap-1">
                        <Star className="size-3" /> По умолчанию
                      </Badge>
                    ) : null}
                    {!p.isActive ? <Badge variant="destructive">Архив</Badge> : null}
                  </div>
                  <p className="text-sm text-on-surface-variant mt-1">
                    {p.legalName}
                  </p>
                  <dl className="text-xs text-on-surface-variant mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                    <div>
                      <dt className="inline">ИНН:</dt>{" "}
                      <dd className="inline font-mono">{p.inn}</dd>
                    </div>
                    {p.kpp ? (
                      <div>
                        <dt className="inline">КПП:</dt>{" "}
                        <dd className="inline font-mono">{p.kpp}</dd>
                      </div>
                    ) : null}
                    {p.ogrn ? (
                      <div>
                        <dt className="inline">ОГРН:</dt>{" "}
                        <dd className="inline font-mono">{p.ogrn}</dd>
                      </div>
                    ) : null}
                    <div>
                      <dt className="inline">НДС:</dt>{" "}
                      <dd className="inline">
                        {p.vatPayer ? `Да (${p.defaultVatRate ?? 0}%)` : "Нет"}
                      </dd>
                    </div>
                  </dl>
                  <p className="text-xs text-on-surface-variant mt-2">
                    Подписант: {p.defaultSignerName}, {p.defaultSignerPosition}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" onClick={() => setEditing(p)}>
                    Редактировать
                  </Button>
                  <Button variant="ghost" onClick={() => onDelete(p)}>
                    Удалить
                  </Button>
                </div>
              </EditorialCard.Body>
            </EditorialCard>
          ))}
        </div>
      )}

      {creating ? (
        <LegalProfileEditDialog
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void refresh();
          }}
        />
      ) : null}

      {editing ? (
        <LegalProfileEditDialog
          mode="edit"
          profile={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}
